import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  jobMonitoringService,
  JOB_STATUS_LABELS,
  type JobStatistics,
  type JobInfo,
} from '../../services/jobMonitoringService';
import {
  ArrowPathIcon,
  CheckCircleIcon,
  XCircleIcon,
  QueueListIcon,
} from '@heroicons/react/24/outline';

export const JobsStatusWidget: React.FC = () => {
  const [stats, setStats] = useState<JobStatistics | null>(null);
  const [recentFailed, setRecentFailed] = useState<JobInfo[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const [statsRes, jobsRes] = await Promise.all([
        jobMonitoringService.getStatistics(),
        jobMonitoringService.getJobs({ status: 'Failed', pageSize: 5, page: 1 }),
      ]);
      setStats(statsRes);
      setRecentFailed(jobsRes.items);
      setError(null);
    } catch {
      setError('Nao foi possivel carregar os jobs.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30_000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">Status de Processamentos</h2>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchData}
            className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
            title="Atualizar"
          >
            <ArrowPathIcon className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          </button>
          <Link
            to="/jobs"
            className="text-xs text-blue-600 hover:underline"
          >
            Ver todos
          </Link>
        </div>
      </div>

      {isLoading && !stats ? (
        <div className="flex items-center justify-center py-6">
          <ArrowPathIcon className="h-6 w-6 animate-spin text-blue-500" />
        </div>
      ) : error ? (
        <div className="text-center py-6 text-sm text-gray-500">{error}</div>
      ) : stats ? (
        <>
          <div className="grid grid-cols-3 gap-3 mb-4">
            <MiniStat
              icon={<ArrowPathIcon className="h-4 w-4 text-blue-600" />}
              label="Processando"
              value={stats.processing}
            />
            <MiniStat
              icon={<QueueListIcon className="h-4 w-4 text-yellow-600" />}
              label="Na fila"
              value={stats.enqueued}
            />
            <MiniStat
              icon={<XCircleIcon className="h-4 w-4 text-red-600" />}
              label="Falhos"
              value={stats.failed}
              highlight={stats.failed > 0}
            />
          </div>

          {recentFailed.length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-500 mb-2">Falhas recentes</p>
              <div className="space-y-1.5 max-h-36 overflow-y-auto">
                {recentFailed.map((job) => (
                  <div
                    key={job.id}
                    className="flex items-center justify-between px-2.5 py-1.5 bg-red-50 rounded text-xs"
                  >
                    <span className="font-medium text-red-800 truncate mr-2">
                      {job.displayName || job.name}
                    </span>
                    <span className="text-red-500 whitespace-nowrap">
                      {JOB_STATUS_LABELS[job.state] ?? job.state}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {recentFailed.length === 0 && stats.failed === 0 && (
            <div className="flex items-center gap-2 text-sm text-green-600 py-2">
              <CheckCircleIcon className="h-5 w-5" />
              Todos os processamentos estão saudáveis.
            </div>
          )}
        </>
      ) : null}
    </div>
  );
};

function MiniStat({
  icon,
  label,
  value,
  highlight,
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
  highlight?: boolean;
}) {
  return (
    <div className={`rounded-lg p-2.5 text-center ${highlight ? 'bg-red-50' : 'bg-gray-50'}`}>
      <div className="flex justify-center mb-1">{icon}</div>
      <p className={`text-lg font-bold ${highlight ? 'text-red-700' : 'text-gray-900'}`}>
        {value.toLocaleString('pt-BR')}
      </p>
      <p className="text-[10px] text-gray-500">{label}</p>
    </div>
  );
}
